<template>
  <div v-show="Display" class="status-viewer">
    <!-- Header -->
    <div class="status-header">
      <button class="back-btn" @click="close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <div class="user-info">
        <div class="avatar">
          <img :src="!user.avatar? require('./img/profile.svg') : bsrlImg + user.avatar" :alt="user.username" />
        </div>
        <div class="user-details">
          <div class="username">{{ user.username }}</div>
          <div class="timestamp">{{ formatRelativeDate(currentStatus.createdAt) }}</div>
        </div>
      </div>

      <button v-if="isMyStatus" class="more-btn" @click="showOptions">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2"/>
          <circle cx="12" cy="12" r="2"/>
          <circle cx="12" cy="19" r="2"/>
        </svg>
      </button>

      <!-- Options Menu -->
      <div v-if="showOptionsMenu" class="options-menu">
        <button @click.stop="deleteStatus" class="option-item delete-option">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
          Delete
        </button>
      </div>

      <!-- Overlay to close options menu -->
      <div v-if="showOptionsMenu" class="options-overlay" @click="toggleOptionsMenu"></div>
    </div>

    <!-- Progress Bar (for multiple statuses) -->
    <div class="progress-bar">
      <div 
        v-for="(item, index) in totalStatuses" 
        :key="index"
        class="progress-segment"
      >
        <div 
          class="progress-fill"
          :class="{ active: index === currentStatusIndex, paused: isPaused && index === currentStatusIndex }"
          :style="{ animationDuration: progressDuration + 's' }"
        ></div>
      </div>
    </div>

    <!-- Status Content -->
    <div class="status-content" 
      @contextmenu.prevent
      @mousedown.stop="handlePauseStart"
      @mouseup.stop="handlePauseEnd"
      @mouseleave="handlePauseEnd"
      @touchstart.stop="handlePauseStart"
      @touchend.stop="handlePauseEnd"
      @touchcancel="handlePauseEnd"
      :class="{ paused: isPaused }"
      :style="{ background: colors[currentStatus.wallpaper || 0]}">
      <!-- Case 1: Text Only -->
      <div v-if="hasText && !hasImage && !hasAudio" class="text-only" >
        <div class="text-content">
          <p>{{ currentStatus.text }}</p>
          <div v-if="currentStatus.author" class="author">{{ currentStatus.author }}</div>
        </div>
      </div>

      <!-- Case 2: Image Only -->
      <div v-if="hasImage && !hasText && !hasAudio" class="image-only"  >
        <img :src="bsrlImg + currentStatus.img" :alt="user.username" />
      </div>

      <!-- Case 3: Audio Only -->
      <div v-if="hasAudio && !hasText && !hasImage" class="audio-only">
        <div class="audio-visualizer">
          <div class="wave-container">
            <div v-for="i in 30" :key="i" class="wave-bar" :style="getWaveStyle(i)"></div>
          </div>
<!--           <button class="play-btn" @click.stop="toggleAudio">
            <svg v-if="!isPlaying" style="min-width: 24px;" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            <svg v-else style="min-width: 24px;" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          </button> -->
        </div>
      </div>

      <!-- Case 4: Text + Image -->
      <div v-if="hasText && hasImage && !hasAudio" class="text-image">
        <img :src="bsrlImg + currentStatus.img" :alt="user.username" />
        <div class="text-overlay">
          <p>{{ currentStatus.text }}</p>
          <div v-if="currentStatus.author" class="author">{{ currentStatus.author }}</div>
        </div>
      </div>

      <!-- Case 5: Text + Audio -->
      <div v-if="hasText && hasAudio && !hasImage" class="text-audio">
        <div class="text-content">
          <p>{{ currentStatus.text }}</p>
          <div v-if="currentStatus.author" class="author">{{ currentStatus.author }}</div>
        </div>
        <div class="audio-overlay">
          <button class="play-btn-overlay" @click.stop="toggleAudio">
            <!-- <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg> -->
            <svg fill="currentColor" width="800px" height="800px" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg">
              <path d="M1129.432 113v1694.148H936.638l-451.773-451.773h-315.45C76.01 1355.375 0 1279.365 0 1185.96V734.187c0-93.404 76.01-169.414 169.415-169.414h315.45L936.638 113h192.794Zm-112.943 112.943h-33.093l-418.68 418.68v630.901l418.68 418.68h33.093V225.944Zm655.488 135.114C1831.904 521.097 1920 733.77 1920 960.107c0 226.226-88.096 438.898-248.023 598.938l-79.851-79.85c138.694-138.695 214.93-323.018 214.93-519.087 0-196.183-76.236-380.506-214.93-519.2Zm-239.112 239.745c95.663 97.018 148.294 224.644 148.294 359.272s-52.631 262.254-148.294 359.272l-80.529-79.286c74.769-75.785 115.88-175.175 115.88-279.986 0-104.811-41.111-204.201-115.88-279.986Zm-981.092 76.914H169.415c-31.06 0-56.472 25.3-56.472 56.471v451.773c0 31.172 25.412 56.472 56.472 56.472h282.358V677.716Z" fill-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Case 6: Image + Audio -->
      <div v-if="hasImage && hasAudio && !hasText" class="image-audio">
        <img :src="bsrlImg + currentStatus.img" :alt="user.username" />
        <div class="audio-overlay">
          <button class="play-btn-overlay" @click.stop="toggleAudio">
            <!-- <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="8 5 19 12 8 19 8 5"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
              <rect x="7" y="5" width="3" height="14"/>
              <rect x="14" y="5" width="3" height="14"/>
            </svg> -->
            <svg fill="currentColor" width="800px" height="800px" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg">
              <path d="M1129.432 113v1694.148H936.638l-451.773-451.773h-315.45C76.01 1355.375 0 1279.365 0 1185.96V734.187c0-93.404 76.01-169.414 169.415-169.414h315.45L936.638 113h192.794Zm-112.943 112.943h-33.093l-418.68 418.68v630.901l418.68 418.68h33.093V225.944Zm655.488 135.114C1831.904 521.097 1920 733.77 1920 960.107c0 226.226-88.096 438.898-248.023 598.938l-79.851-79.85c138.694-138.695 214.93-323.018 214.93-519.087 0-196.183-76.236-380.506-214.93-519.2Zm-239.112 239.745c95.663 97.018 148.294 224.644 148.294 359.272s-52.631 262.254-148.294 359.272l-80.529-79.286c74.769-75.785 115.88-175.175 115.88-279.986 0-104.811-41.111-204.201-115.88-279.986Zm-981.092 76.914H169.415c-31.06 0-56.472 25.3-56.472 56.471v451.773c0 31.172 25.412 56.472 56.472 56.472h282.358V677.716Z" fill-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Case 7: Text + Image + Audio -->
      <div v-if="hasText && hasImage && hasAudio" class="text-image-audio">
        <img :src="bsrlImg + currentStatus.img" :alt="user.username" />
        <div class="content-overlay">
          <div class="text-section">
            <p>{{ currentStatus.text }}</p>
            <div v-if="currentStatus.author" class="author">{{ currentStatus.author }}</div>
          </div>
        </div>
        <div class="audio-overlay">
          <button class="play-btn-overlay" @click.stop="toggleAudio">
          <!--  <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="8 5 19 12 8 19 8 5"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor">
              <rect x="7" y="5" width="3" height="14"/>
              <rect x="14" y="5" width="3" height="14"/>
            </svg> -->
            <svg fill="currentColor" width="800px" height="800px" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg">
              <path d="M1129.432 113v1694.148H936.638l-451.773-451.773h-315.45C76.01 1355.375 0 1279.365 0 1185.96V734.187c0-93.404 76.01-169.414 169.415-169.414h315.45L936.638 113h192.794Zm-112.943 112.943h-33.093l-418.68 418.68v630.901l418.68 418.68h33.093V225.944Zm655.488 135.114C1831.904 521.097 1920 733.77 1920 960.107c0 226.226-88.096 438.898-248.023 598.938l-79.851-79.85c138.694-138.695 214.93-323.018 214.93-519.087 0-196.183-76.236-380.506-214.93-519.2Zm-239.112 239.745c95.663 97.018 148.294 224.644 148.294 359.272s-52.631 262.254-148.294 359.272l-80.529-79.286c74.769-75.785 115.88-175.175 115.88-279.986 0-104.811-41.111-204.201-115.88-279.986Zm-981.092 76.914H169.415c-31.06 0-56.472 25.3-56.472 56.471v451.773c0 31.172 25.412 56.472 56.472 56.472h282.358V677.716Z" fill-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
      <!-- Click zones for navigation -->
      <div class="nav-zone nav-left"></div>
      <div class="nav-zone nav-right"></div>
    </div>

    <!-- Bottom Actions -->
    <div class="status-actions">
      <div class="stats">
        <div class="stat-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span>{{ formatNumber(currentStatus.vue?.length) }}</span>
        </div>
        <div class="stat-item clickable" @click="toggleLikesList">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>{{ formatNumber(currentStatus.likes?.length) }}</span>
        </div>
      </div>

      <button
        v-if="!isMyStatus"
        class="like-btn" 
        :class="{ liked: currentStatusLiked }"
        @click="currentStatusLiked ? likeStatus(currentStatusIndex,'unlikeStatus') : likeStatus(currentStatusIndex,'likeStatus')"
      >
        <svg viewBox="0 0 24 24" :fill="currentStatus.isLiked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
    </div>

    <!-- Likes Sliding Menu -->
    <div :class="['likes-menu', { open: showLikesMenu }]">
      <div class="likes-menu-handle" @click="toggleLikesList">
        <div class="handle-bar"></div>
      </div>
      <div class="likes-menu-header">
        <h3>Likes</h3>
        <span class="likes-count">{{ currentStatus.likes?.length }}</span>
      </div>
      <div class="likes-menu-content">
        <div v-for="like in currentStatus.likes" :key="like._id" class="like-person">
          <div class="like-avatar">
            <img :src="!allUsers[like.userId]?.profilImgMini1 ? require('./img/profile.svg'): bsrlImg+allUsers[like.userId]?.profilImgMini1" 
            />
          </div>
          <div class="like-name">{{ allUsers[like.userId]?.pseudo }}</div>
        </div>
        <div v-if="currentStatus.likes?.length === 0" class="no-likes">
          No likes yet
        </div>
      </div>
    </div>

    <!-- Overlay to close menu -->
    <div v-if="showLikesMenu" class="likes-overlay" @click="toggleLikesList"></div>
    <Loader :loading="loading" />
  </div>
</template>

<script src="./status.js">
</script>
<style src="./status.css" scoped>
</style>